/**
 * CSV upload connector.
 *
 * The fifth "channel" — agency-defined custom data. Buyers upload a CSV
 * with a date column + arbitrary metric columns. We parse, infer column
 * types heuristically, and the result feeds the PDF templates the same
 * way the platform-OAuth connectors do.
 *
 * Pure functions: no network, no IndexedDB, no DOM. The SPA layer is
 * responsible for persisting parsed uploads.
 */

import type {
  CSVColumnSpec,
  CSVReportData,
  CSVUploadMeta,
} from "@agencylens/shared";

export class CSVError extends Error {
  constructor(
    message: string,
    public readonly hint?: string,
  ) {
    super(message);
    this.name = "CSVError";
  }
}

/* ----------------------------------------------------- RFC-4180-ish parser */

/**
 * Parses a CSV string into an array of cell-arrays. Handles:
 *   - Quoted fields with embedded commas, newlines, and "" escapes
 *   - LF or CRLF line endings
 *   - Trailing blank lines
 *   - Mixed quoting (quoted + unquoted in same row)
 *
 * Does NOT handle:
 *   - Custom delimiters (only comma)
 *   - Comment markers
 *   - BOM-prefixed UTF-8 (the caller should strip BOM first if needed)
 *
 * Returns an empty array if the input has no non-empty lines.
 */
export function parseCsvCells(text: string): string[][] {
  // Strip BOM if present
  let input = text;
  if (input.charCodeAt(0) === 0xfeff) input = input.slice(1);

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  let i = 0;

  while (i < input.length) {
    const ch = input[i]!;

    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          // Escaped double-quote
          cell += '"';
          i += 2;
          continue;
        } else {
          inQuotes = false;
          i++;
          continue;
        }
      } else {
        cell += ch;
        i++;
        continue;
      }
    }

    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ",") {
      row.push(cell);
      cell = "";
      i++;
      continue;
    }
    if (ch === "\n" || ch === "\r") {
      row.push(cell);
      cell = "";
      // Skip \r\n as one line break
      if (ch === "\r" && input[i + 1] === "\n") i++;
      i++;
      // Don't push empty trailing rows (single \r\n at EOF)
      if (row.length === 1 && row[0] === "") {
        row = [];
        continue;
      }
      rows.push(row);
      row = [];
      continue;
    }
    cell += ch;
    i++;
  }

  // Final field
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    if (!(row.length === 1 && row[0] === "")) rows.push(row);
  }

  return rows;
}

/* ----------------------------------------------------- type inference */

const DATE_PATTERNS: RegExp[] = [
  /^\d{4}-\d{2}-\d{2}$/, // 2026-05-26
  /^\d{4}\/\d{2}\/\d{2}$/, // 2026/05/26
  /^\d{2}\/\d{2}\/\d{4}$/, // 05/26/2026 or 26/05/2026
  /^\d{2}-\d{2}-\d{4}$/, // 26-05-2026
  /^\d{1,2}\s+\w{3,}\s+\d{4}$/, // 26 May 2026
];

function looksLikeDate(s: string): boolean {
  const v = s.trim();
  if (!v) return false;
  return DATE_PATTERNS.some((p) => p.test(v));
}

function looksLikeNumber(s: string): boolean {
  const v = s.trim().replace(/,/g, "");
  if (!v) return false;
  const n = Number(v);
  return Number.isFinite(n);
}

function looksLikePercent(s: string): boolean {
  const v = s.trim();
  if (!v.endsWith("%")) return false;
  return looksLikeNumber(v.slice(0, -1));
}

const CURRENCY_PREFIX_RE = /^([£$€¥₹]|USD|EUR|GBP|JPY|INR|AUD|CAD|CHF)\s*-?\d/;

function looksLikeCurrency(s: string): boolean {
  const v = s.trim();
  return CURRENCY_PREFIX_RE.test(v);
}

function inferColumnType(samples: string[]): CSVColumnSpec["type"] {
  const non = samples.filter((s) => s.trim().length > 0);
  if (non.length === 0) return "string";

  const tally = { date: 0, percent: 0, currency: 0, number: 0, string: 0 };
  for (const v of non) {
    if (looksLikeDate(v)) tally.date++;
    else if (looksLikePercent(v)) tally.percent++;
    else if (looksLikeCurrency(v)) tally.currency++;
    else if (looksLikeNumber(v)) tally.number++;
    else tally.string++;
  }
  // Require >=80% confidence for a typed inference, else fall back to string
  const threshold = Math.ceil(non.length * 0.8);
  if (tally.date >= threshold) return "date";
  if (tally.percent >= threshold) return "percent";
  if (tally.currency >= threshold) return "currency";
  if (tally.number >= threshold) return "number";
  return "string";
}

/** Coerces a cell value to its inferred-type primitive representation. */
function coerceCell(value: string, type: CSVColumnSpec["type"]): string | number {
  const v = value.trim();
  switch (type) {
    case "date":
      return v; // Keep as-is; templates render the original string
    case "number":
      return Number(v.replace(/,/g, "")) || 0;
    case "percent": {
      const num = Number(v.replace("%", "").trim());
      return Number.isFinite(num) ? num / 100 : 0;
    }
    case "currency": {
      const num = Number(v.replace(CURRENCY_PREFIX_RE, "").replace(/,/g, ""));
      // CURRENCY_PREFIX_RE consumes the symbol and following digits; redo
      const stripped = v.replace(/[£$€¥₹]|USD|EUR|GBP|JPY|INR|AUD|CAD|CHF/g, "").replace(/,/g, "").trim();
      const n = Number(stripped);
      return Number.isFinite(n) ? n : Number.isFinite(num) ? num : 0;
    }
    case "string":
    default:
      return v;
  }
}

/* ----------------------------------------------------- parseCsv */

export interface ParseResult {
  meta: CSVUploadMeta;
  rows: Array<Record<string, string | number>>;
}

let _idCounter = 0;
function makeUploadId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  // Fallback: timestamp + counter (sufficient for the SPA, never colliding within one session)
  _idCounter++;
  return `csv_${Date.now().toString(36)}_${_idCounter}`;
}

/**
 * Parses raw CSV text + filename into a typed upload record.
 *
 * The returned `meta.columns` reflect inferred types. The SPA may then offer
 * the user a chance to override types before passing both to `buildReport`.
 */
export function parseCsv(text: string, fileName = "uploaded.csv"): ParseResult {
  const cells = parseCsvCells(text);
  if (cells.length < 1) {
    throw new CSVError("CSV is empty.", "Upload a file with at least a header row.");
  }
  const header = cells[0]!.map((h) => h.trim());
  if (header.length === 0) {
    throw new CSVError("CSV header row is empty.");
  }
  // Deduplicate empty headers as col_N
  const seen = new Map<string, number>();
  for (let i = 0; i < header.length; i++) {
    let name = header[i] || `col_${i + 1}`;
    if (seen.has(name)) {
      const n = seen.get(name)! + 1;
      seen.set(name, n);
      name = `${name}_${n}`;
    } else {
      seen.set(name, 1);
    }
    header[i] = name;
  }

  const rawRows = cells.slice(1).filter((r) => r.some((c) => c.trim().length > 0));

  // Sample up to 50 rows per column for type inference
  const sample = rawRows.slice(0, 50);
  const columns: CSVColumnSpec[] = header.map((h, idx) => ({
    header: h,
    label: h,
    type: inferColumnType(sample.map((r) => r[idx] ?? "")),
  }));

  const rows = rawRows.map((r) => {
    const obj: Record<string, string | number> = {};
    for (let i = 0; i < header.length; i++) {
      const colType = columns[i]?.type ?? "string";
      obj[header[i]!] = coerceCell(r[i] ?? "", colType);
    }
    return obj;
  });

  const meta: CSVUploadMeta = {
    uploadId: makeUploadId(),
    fileName,
    uploadedAt: new Date().toISOString(),
    rowCount: rows.length,
    columns,
  };
  return { meta, rows };
}

/* ----------------------------------------------------- buildReport */

/**
 * Build a CSVReportData from a parsed upload, applying optional
 * caller-supplied column type overrides and selection of date /
 * metric columns.
 *
 * If `dateColumn` is omitted, picks the first column with `type === "date"`.
 * If `metricColumns` is omitted, picks every non-string column except the
 * date column.
 */
export interface BuildReportOptions {
  upload: CSVUploadMeta;
  rows: Array<Record<string, string | number>>;
  columnOverrides?: CSVColumnSpec[];
  dateColumn?: string;
  metricColumns?: string[];
}

export function buildReport(opts: BuildReportOptions): CSVReportData {
  const { rows, columnOverrides } = opts;
  // Apply overrides
  const overrideMap = new Map(columnOverrides?.map((c) => [c.header, c]) ?? []);
  const columns = opts.upload.columns.map((c) => overrideMap.get(c.header) ?? c);
  const upload: CSVUploadMeta = { ...opts.upload, columns };

  const dateColumn =
    opts.dateColumn ?? columns.find((c) => c.type === "date")?.header;
  if (!dateColumn) {
    throw new CSVError(
      "Could not determine a date column.",
      "Set the date column explicitly via buildReport(... { dateColumn })",
    );
  }
  const metricColumns =
    opts.metricColumns ??
    columns
      .filter((c) => c.header !== dateColumn && c.type !== "string")
      .map((c) => c.header);

  // If overrides changed types, re-coerce affected rows
  if (columnOverrides && columnOverrides.length > 0) {
    const changed = columnOverrides.filter(
      (c) => opts.upload.columns.find((u) => u.header === c.header)?.type !== c.type,
    );
    if (changed.length > 0) {
      for (const row of rows) {
        for (const ovr of changed) {
          const raw = String(row[ovr.header] ?? "");
          row[ovr.header] = coerceCell(raw, ovr.type);
        }
      }
    }
  }

  return {
    upload,
    dateColumn,
    metricColumns,
    rows,
    fetchedAt: new Date().toISOString(),
  };
}

/* ----------------------------------------------------- helpers */

/** Read a File (browser) into text. SPA helper, but useful here too. */
export async function readFileAsText(file: File): Promise<string> {
  return file.text();
}
